import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';

import {
  CampaignStatus,
  CampaignTrackingItemType,
  type Campaign,
  type CampaignDetailData,
  type CampaignTrackHandle,
  type CampaignTrackPost,
} from '@/entities/campaign';
import { Tabs, TabsContent } from '@/shared/components/shadcn/tabs';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useMemo, useState } from 'react';
import {
  getCampaignEditorCreatorIssues,
  getCampaignEditorSubmitValidationIssues,
} from '../../campaign-editor/helpers/campaign-editor-validation';
import { useCampaignEditorDraft } from '../../campaign-editor/hooks/use-campaign-editor-draft';
import { CampaignBasicsSection } from '../../campaign-editor/ui/basic-section/campaign-basics.section';
import { CampaignCreatorsSection } from '../../campaign-editor/ui/creator-section/campaign-creators.section';
import { CampaignPostsSection } from '../../campaign-editor/ui/post-section/campaign-posts.section';

import { getFooterSecondaryAction } from '../../campaign-editor/helpers/footer-secondary-action';
import { CampaignEditorPhaseTabs } from '../../campaign-editor/primitives';
import { CampaignEditorProvider } from '../../campaign-editor/store/campaign-editor-provider';
import type { CampaignEditorSection } from '../../campaign-editor/types';
import { CampaignEditorFooter } from '../../campaign-editor/ui/campaign-editor-footer';
import { buildCampaignEditInitialValues } from './helpers/build-campaign-edit-initial-valutes';
import {
  getCampaignEditFooterViewModel,
  getCampaignEditSectionForValidationIssue,
} from './helpers/campaign-edit-dialog-view-model';
import { useCampaignEditSubmit } from './hooks/use-campaign-edit-submit';
import { SupportedPlatformBadge } from '../../ui/supported-platform-badge';

type CampaignEditDialogProps = {
  campaign: CampaignDetailData;
  campaignId: string;
  initialSection: CampaignEditorSection;
  isOpen: boolean;
  onClose: () => void;
};

export function CampaignEditDialog({
  campaign,
  campaignId,
  isOpen,
  initialSection,
  onClose,
}: CampaignEditDialogProps) {
  const { workspace } = useWorkspace();
  const creators = useMemo(
    () =>
      campaign.items.filter(
        (item): item is CampaignTrackHandle => item.type === CampaignTrackingItemType.HANDLE,
      ),
    [campaign.items],
  );
  const posts = useMemo(
    () =>
      campaign.items.filter(
        (item): item is CampaignTrackPost => item.type === CampaignTrackingItemType.POST,
      ),
    [campaign.items],
  );
  const initialValues = useMemo(
    () =>
      buildCampaignEditInitialValues({
        campaign,
        creators,
        posts,
        workspaceTimezone: workspace.timezone,
      }),
    [campaign, creators, posts, workspace.timezone],
  );

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[calc(100vh-48px)] w-[calc(100vw-48px)] max-w-[908px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[908px]"
      >
        <DialogHeader className="px-6 pt-5 pr-14 pb-3">
          <DialogTitle className="flex items-center gap-3">
            Edit setting
            <SupportedPlatformBadge />
          </DialogTitle>
          <DialogDescription>
            Manage campaign details, creator requirements, keywords, and manually tracked post URLs.
          </DialogDescription>
        </DialogHeader>

        <CampaignEditorProvider initialValues={initialValues}>
          <EditDialogEditorContent
            campaign={campaign}
            campaignItemSlotLimit={workspace.constraints.campaign_item_slot_limit}
            campaignId={campaignId}
            creators={creators}
            initialSection={initialSection}
            onClose={onClose}
            posts={posts}
            workspaceTimezone={workspace.timezone}
          />
        </CampaignEditorProvider>
      </DialogContent>
    </Dialog>
  );
}

const EditDialogEditorContent = ({
  campaign,
  campaignItemSlotLimit,
  campaignId,
  creators,
  initialSection,
  onClose,
  posts,
  workspaceTimezone,
}: {
  campaign: Campaign;
  campaignItemSlotLimit: number;
  campaignId: string;
  creators: CampaignTrackHandle[];
  initialSection: CampaignEditorSection;
  onClose: () => void;
  posts: CampaignTrackPost[];
  workspaceTimezone?: string;
}) => {
  const [activeSection, setActiveSection] = useState<CampaignEditorSection>(initialSection);
  const [creatorsValidationRequest, setCreatorsValidationRequest] = useState(0);
  const draft = useCampaignEditorDraft(campaignItemSlotLimit);
  const fieldPolicy = {
    startDateEditable: campaign.status === CampaignStatus.SCHEDULED,
  };
  const submitState = useCampaignEditSubmit({
    campaign,
    campaignId,
    creators,
    posts,
    callback: onClose,
    workspaceTimezone,
  });
  const payload = draft.serialize();
  const creatorIssues = getCampaignEditorCreatorIssues(payload.creatorItems);
  const validationIssues = getCampaignEditorSubmitValidationIssues({
    campaignItemSlotLimit,
    payload,
    startDateEditable: fieldPolicy.startDateEditable,
    workspaceTimezone,
  });
  const footer = getCampaignEditFooterViewModel({
    campaignItemSlotLimit,
    isSubmitting: submitState.isSubmitting,
    totalTrackedItems: draft.summary.totalTrackedItems,
    validationIssue: validationIssues[0],
    validationIssueCount: validationIssues.length,
  });
  const footerSecondaryAction = getFooterSecondaryAction([activeSection, setActiveSection]);

  const submit = async () => {
    if (creatorIssues.length > 0) {
      setCreatorsValidationRequest((count) => count + 1);
    }

    if (validationIssues.length > 0 || submitState.isSubmitting) {
      if (validationIssues[0]) {
        setActiveSection(getCampaignEditSectionForValidationIssue(validationIssues[0]));
      }
      return;
    }

    await submitState.submitCampaignUpdate(draft.serialize());
  };

  return (
    <>
      <div className="flex-1 overflow-auto">
        <Tabs
          value={activeSection}
          onValueChange={(value) => {
            setActiveSection(value as CampaignEditorSection);
          }}
          className="min-h-0 flex-1 gap-0"
        >
          <div className="border-b py-3 pr-14 pl-6">
            <CampaignEditorPhaseTabs setupReady />
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-6"
            style={{ scrollbarGutter: 'stable' }}
          >
            <div className="mx-auto flex w-full max-w-[860px] flex-col gap-8 py-8">
              <TabsContent value="campaign-info" className="mt-0">
                <CampaignBasicsSection
                  draft={draft.basics}
                  initialCampaignName={campaign.name}
                  startDateEditable={fieldPolicy.startDateEditable}
                />
              </TabsContent>
              <TabsContent value="creators" className="mt-0">
                <CampaignCreatorsSection
                  draft={draft.creators}
                  campaignItemSlotLimit={campaignItemSlotLimit}
                  remainingSlots={draft.summary.remainingSlots}
                  validationRequest={creatorsValidationRequest}
                />
              </TabsContent>
              <TabsContent value="post-urls" className="mt-0">
                <CampaignPostsSection
                  draft={draft.posts}
                  campaignItemSlotLimit={campaignItemSlotLimit}
                  remainingSlots={draft.summary.remainingSlots}
                />
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
      <DialogFooter className="bg-background/95 border-t px-6 py-4 backdrop-blur sm:items-center sm:justify-between">
        <CampaignEditorFooter
          message={footer.message}
          usageLabel={footer.usageLabel}
          secondaryAction={footerSecondaryAction}
          primaryAction={{
            label: footer.primaryLabel,
            disabled: footer.primaryDisabled,
            onClick: () => void submit(),
          }}
        />
      </DialogFooter>
    </>
  );
};
